import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useFetchAllBanks, useCreateBank } from "@accountHooks";
import { CreatableSelect } from "@/components/creatable-select/CreatableSelect";
import { BankAccountFormSection } from "@/components/bank-account-form-section/BankAccountFormSection";
import { Info } from "lucide-react";
import { accountFormSchema } from "./accountForm.schema";
import {
  ACCOUNT_TYPES,
  CARD_SCHEMES,
  MOBILE_MONEY_PROVIDERS,
  STATUSES,
} from "@/lib/constants";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import {Account} from "@/integrations/supabase/account.models.ts";

export type AccountFormData = z.infer<typeof accountFormSchema>;

interface AccountFormProps {
  account?: Account;
  onSave: (data: AccountFormData) => void | Promise<void>;
  onCancel: () => void;
}

export function AccountForm({ account, onSave, onCancel }: AccountFormProps) {
  const { toast } = useToast();
  const { data: banks = [], isLoading: banksLoading } = useFetchAllBanks();
  const createBank = useCreateBank();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AccountFormData>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      name: account?.name ?? "",
      nickname: account?.nickname ?? "",
      account_number: account?.account_number ?? "",
      type: (account?.type as AccountFormData["type"]) ?? "bank",
      currency: account?.currency ?? "USD",
      balance: account?.balance ?? 0,
      status: (account?.status as AccountFormData["status"]) ?? "active",
      subtype: (account?.subtype as AccountFormData["subtype"]) ?? undefined,
      bank_name: account?.bank_name ?? "",
      mobile_money_provider: account?.mobile_money_provider ?? "",
      mobile_money_provider_other: "",
      card_provider: account?.card_provider ?? "",
      card_scheme:
        (account?.card_scheme as AccountFormData["card_scheme"]) ?? undefined,
      interest_rate: account?.interest_rate ?? null,
      sort_code: "",
      iban: "",
      swift_code: "",
      routing_number: "",
      transit_number: "",
      institution_number: "",
      bank_address: "",
    },
  });
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();
  const type = watch("type");
  const subtype = watch("subtype");
  const currency = watch("currency");
  const balance = watch("balance");
  const interestRate = watch("interest_rate");
  const mobileMoneyProvider = watch("mobile_money_provider");

  const onSubmit = async (data: AccountFormData) => {
    try {
      // Prepare data for submission
      const submitData = { ...data };

      // If mobile money provider is 'Other', use the custom value
      if (
        data.type === "mobile_money" &&
        data.mobile_money_provider === "Other"
      ) {
        submitData.mobile_money_provider = data.mobile_money_provider_other;
      }

      // Remove the temporary field
      delete submitData.mobile_money_provider_other;

      await onSave(submitData);
      toast({
        title: "Success",
        description: `Account ${account ? "updated" : "created"} successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${account ? "update" : "create"} account. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Account Name</Label>
        <Input
          id="name"
          {...register("name")}
          placeholder="e.g. Main Checking"
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="nickname">
          Nickname{" "}
          <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <Input
          id="nickname"
          {...register("nickname")}
          placeholder="e.g. My Main Savings"
          maxLength={50}
        />
        {errors.nickname && (
          <p className="text-sm text-destructive">{errors.nickname.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="account_number">Account Number</Label>
        <Input
          id="account_number"
          {...register("account_number")}
          placeholder="e.g. 1234567890"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Account Type</Label>
        <Select
          value={type}
          onValueChange={(value) => {
            setValue("type", value as AccountFormData["type"]);
            // Reset type-specific fields when type changes
            setValue("subtype", undefined);
            setValue("bank_name", "");
            setValue("mobile_money_provider", "");
            setValue("mobile_money_provider_other", "");
            setValue("card_provider", "");
            setValue("card_scheme", undefined);
            setValue("interest_rate", null);
            setValue("sort_code", "");
            setValue("iban", "");
            setValue("swift_code", "");
            setValue("routing_number", "");
            setValue("transit_number", "");
            setValue("institution_number", "");
            setValue("bank_address", "");
          }}>
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCOUNT_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bank Account Subtype Selection */}
      {type === "bank" && (
        <div className="space-y-2">
          <Label htmlFor="subtype">Account Subtype</Label>
          <Select
            value={subtype}
            onValueChange={(value) => {
              setValue("subtype", value as AccountFormData["subtype"]);
              // Reset interest rate when switching from savings to current
              if (value === "current") {
                setValue("interest_rate", null);
              }
            }}>
            <SelectTrigger id="subtype">
              <SelectValue placeholder="Select subtype" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="savings">
                <div className="flex items-center gap-2">
                  Savings
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Savings accounts typically earn interest and are
                          designed for storing money
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </SelectItem>
              <SelectItem value="current">
                <div className="flex items-center gap-2">
                  Current
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Current accounts are for everyday transactions and
                          typically don't earn interest
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Bank Name Field */}
      {type === "bank" && (
        <div className="space-y-2">
          <Label htmlFor="bank_name">Bank Name *</Label>
          <CreatableSelect
            id="bank_name"
            value={watch("bank_name") ?? ""}
            onValueChange={(val) => {
              setValue("bank_name", val);
              const bank = banks.find((b) => b.name === val);
              setValue("bank_address", bank?.address ?? "");
            }}
            options={banks.map((b) => ({ value: b.name, label: b.name }))}
            placeholder="Select or create a bank..."
            disabled={banksLoading}
            onCreate={async (name) => {
              const bank = await createBank.mutateAsync({
                name,
                address: watch("bank_address"),
              });
              return bank.name;
            }}
            aria-required={true}
            aria-describedby="bank_name-error"
            aria-invalid={!!errors.bank_name}
          />
          {errors.bank_name && (
            <p id="bank_name-error" className="text-sm text-destructive">
              {errors.bank_name.message}
            </p>
          )}
        </div>
      )}

      {/* Bank Account Details */}
      {type === "bank" && (
        <BankAccountFormSection register={register} errors={errors} />
      )}

      {/* Bank Address Field */}
      {type === "bank" && (
        <div className="space-y-2">
          <Label htmlFor="bank_address">Bank Address</Label>
          <Input
            id="bank_address"
            {...register("bank_address")}
            placeholder="e.g. 123 Main St, London, UK"
          />
          {errors.bank_address && (
            <p className="text-sm text-destructive">
              {errors.bank_address.message}
            </p>
          )}
        </div>
      )}

      {/* Mobile Money Provider Selection */}
      {type === "mobile_money" && (
        <div className="space-y-2">
          <Label htmlFor="mobile_money_provider">Mobile Money Provider *</Label>
          <Select
            value={mobileMoneyProvider}
            onValueChange={(value) => {
              setValue("mobile_money_provider", value);
              // Clear custom provider when switching away from 'Other'
              if (value !== "Other") {
                setValue("mobile_money_provider_other", "");
              }
            }}>
            <SelectTrigger id="mobile_money_provider">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {MOBILE_MONEY_PROVIDERS.map((provider) => (
                <SelectItem key={provider} value={provider}>
                  {provider}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.mobile_money_provider && (
            <p className="text-sm text-destructive">
              {errors.mobile_money_provider.message}
            </p>
          )}
        </div>
      )}

      {/* Custom Mobile Money Provider Input */}
      {type === "mobile_money" && mobileMoneyProvider === "Other" && (
        <div className="space-y-2">
          <Label htmlFor="mobile_money_provider_other">Provider Name *</Label>
          <Input
            id="mobile_money_provider_other"
            {...register("mobile_money_provider_other")}
            placeholder="Enter provider name"
            maxLength={100}
          />
          {errors.mobile_money_provider_other && (
            <p className="text-sm text-destructive">
              {errors.mobile_money_provider_other.message}
            </p>
          )}
        </div>
      )}

      {/* Card Provider Field */}
      {type === "card" && (
        <div className="space-y-2">
          <Label htmlFor="card_provider">Card Provider *</Label>
          <Input
            id="card_provider"
            {...register("card_provider")}
            placeholder="e.g. Chase, Bank of America"
            maxLength={100}
          />
          {errors.card_provider && (
            <p className="text-sm text-destructive">
              {errors.card_provider.message}
            </p>
          )}
        </div>
      )}

      {/* Card Scheme Selection */}
      {type === "card" && (
        <div className="space-y-2">
          <Label htmlFor="card_scheme">Card Scheme *</Label>
          <Select
            value={watch("card_scheme")}
            onValueChange={(value) =>
              setValue("card_scheme", value as AccountFormData["card_scheme"])
            }>
            <SelectTrigger id="card_scheme">
              <SelectValue placeholder="Select card scheme" />
            </SelectTrigger>
            <SelectContent>
              {CARD_SCHEMES.map((scheme) => (
                <SelectItem key={scheme} value={scheme}>
                  {scheme.charAt(0).toUpperCase() + scheme.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.card_scheme && (
            <p className="text-sm text-destructive">
              {errors.card_scheme.message}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select
            value={currency}
            onValueChange={(value) => setValue("currency", value)}>
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="balance">Balance</Label>
          <Input
            id="balance"
            type="number"
            step="0.01"
            readOnly={!!account}
            aria-describedby={account ? "balance-hint" : undefined}
            {...register("balance", { valueAsNumber: true })}
          />
          {account && (
            <p id="balance-hint" className="text-sm text-muted-foreground">
              The balance changes only through transactions. To correct it, add an income or expense
              transaction.
            </p>
          )}
          {errors.balance && (
            <p className="text-sm text-destructive">{errors.balance.message}</p>
          )}
        </div>
      </div>

      {account && (
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={watch("status")}
            onValueChange={(value) =>
              setValue("status", value as AccountFormData["status"])
            }>
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {type === "bank" && subtype === "savings" && (
        <div className="space-y-2">
          <Label htmlFor="interest_rate">Interest Rate (% p.a.)</Label>
          <Input
            id="interest_rate"
            type="number"
            step="0.01"
            {...register("interest_rate", {
              valueAsNumber: true,
              setValueAs: (v) => (v === "" ? null : parseFloat(v)),
            })}
            placeholder="e.g. 5.5"
          />
          {errors.interest_rate && (
            <p className="text-sm text-destructive">
              {errors.interest_rate.message}
            </p>
          )}
          {interestRate && interestRate > 0 && balance > 0 && (
            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                Monthly interest (compound):{" "}
                {(
                  balance *
                  (Math.pow(1 + interestRate / 100 / 12, 1) - 1)
                ).toLocaleString(undefined, {
                  style: "currency",
                  currency,
                })}
              </p>
              <p>
                Yearly interest (compound):{" "}
                {(
                  balance *
                  (Math.pow(1 + interestRate / 100 / 12, 12) - 1)
                ).toLocaleString(undefined, {
                  style: "currency",
                  currency,
                })}
              </p>
            </div>
          )}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{account ? "Update" : "Create"}</Button>
      </DialogFooter>
    </form>
  );
}
